import React, { useEffect, useState } from 'react';
import { useBunkerContext } from 'src/context/BunkerContext';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { EngineDataTypeEnum } from 'src/types/ViewModel/AolEnums';
import ConsumptionData from './ConsumptionData';
import { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import AddConsumptionRow from './AddConsumptionRow';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { getPreviousAolItem, isBunkeringUsableAtDate } from 'src/components/bunkering/BunkeringUtils';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { AolDataItem } from 'src/types/ViewModel/AolItem';
import { useAolStore } from 'src/context/AolContext';
import { WarningText } from 'src/components/shared/warning-text/WarningText.style';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { useVesselStore } from 'src/context/VesselContext';
import { CostWrapper, FuelTypeHeaderStyle } from './Consumption.style';
import { UUID } from 'crypto';
import { compareTanks } from 'src/components/bunkering/bunker-rob-page/RobUtils';

interface Props {
    aolItem: AolDataItem;
    engineType: EngineDataTypeEnum;
    consumption: Consumption[];
    isReadOnly: boolean;
    onChange: (inputData: InputChangeData) => void;
    addConsumptionRow: (consumption: Consumption[]) => void;
}

const ConsumptionContainer: React.FC<Props> = ({ aolItem, engineType, consumption, isReadOnly, onChange, addConsumptionRow }) => {
    const { bunkerings, fuelRobData } = useBunkerContext();
    const { aolDataItems } = useAolStore();
    const { vessel } = useVesselStore();
    const [availableFuel, setAvailableFuel] = useState<Bunkering[]>([]);
    const [previousRobs, setPreviousRobs] = useState<FuelROB[]>([]);

    useEffect(() => {
        if (bunkerings && bunkerings.length > 0) {
            let _bunkerings = [] as Bunkering[];
            if (
                engineType === EngineDataTypeEnum.Incinerator ||
                engineType === EngineDataTypeEnum.InertGasGenerator ||
                engineType === EngineDataTypeEnum.DieselGasOilDrivenPowerPack
            ) {
                _bunkerings = bunkerings.filter((b) => b.fuelType === FuelType.LGO && isBunkeringUsableAtDate(b, aolItem?.reportDate));
            } else {
                _bunkerings = bunkerings.filter((b) => isBunkeringUsableAtDate(b, aolItem?.reportDate));
            }
            setAvailableFuel(_bunkerings);
        } else setAvailableFuel([]);

        if (fuelRobData && aolItem) {
            const previousNoonReportId = getPreviousAolItem(aolItem, aolDataItems)?.noonReport?.metadata?.noonReportId;
            const previousRobs = fuelRobData.filter((f) => previousNoonReportId && f.noonReportId === previousNoonReportId);
            setPreviousRobs(previousRobs);
        } else setPreviousRobs([]);
    }, [bunkerings, fuelRobData]);

    const getAvailableRob = (bunkeringGuid: string, tankId?: number): number => {
        const previousNoonReportId = getPreviousAolItem(aolItem, aolDataItems)?.noonReport?.metadata?.noonReportId;
        const fuelRobs = fuelRobData?.filter(
            (f) => previousNoonReportId && f.noonReportId === previousNoonReportId && f.bunkeringGuid === bunkeringGuid
        );
        return tankId ? fuelRobs?.find((f) => f.tankId === tankId)?.rob : fuelRobs?.reduce((acc, curr) => acc + curr.rob, 0);
    };

    const getBunkeringDetails = (bunkering: Bunkering | null, tankId: number): string => {
        const details = `${vessel.nonCargoTanks.find((t) => t.tankId === tankId)?.tankName ?? tankId}`;
        const availableRob = getAvailableRob(bunkering?.bunkeringGuid ?? '', tankId);
        if (availableRob !== null && availableRob !== undefined) return `${details} (ROB: ${availableRob} MT)`;
        return details;
    };

    const checkTotalConsumptionTotalRob = (bunkeringGuid: UUID): boolean => {
        const totalCons =
            parseFloat(
                consumption
                    ?.filter((c) => c.bunkeringGuid === bunkeringGuid)
                    ?.reduce((acc, curr) => acc + curr.consumptionMt, 0)
                    ?.toFixed(3)
            ) ?? 0;
        if (totalCons > getAvailableRob(bunkeringGuid)) {
            return true;
        }
        return false;
    };

    return (
        <div>
            {!isReadOnly && (
                <AddConsumptionRow
                    noonReportId={aolItem?.noonReport?.metadata?.noonReportId ?? 0}
                    engineDataType={engineType}
                    bunkerings={availableFuel}
                    consumption={consumption?.filter((c) => c.engineDataType === engineType)}
                    addConsumptionRow={addConsumptionRow}
                    fuelRobData={previousRobs}
                />
            )}
            {Array.from(new Set(consumption?.filter((c) => c.engineDataType === engineType)?.map((c) => c.bunkeringGuid)))?.map((bunkeringGuid) => {
                return (
                    <div key={bunkeringGuid}>
                        <FuelTypeHeaderStyle>
                            {bunkerings.find((b) => b.bunkeringGuid === bunkeringGuid)?.bunkeringReference ?? 'Unknown BDN'}
                        </FuelTypeHeaderStyle>
                        {checkTotalConsumptionTotalRob(bunkeringGuid) && (
                            <WarningText>
                                Total consumption of {bunkerings.find((b) => b.bunkeringGuid === bunkeringGuid)?.bunkeringReference} exceeds available
                                ROB.
                            </WarningText>
                        )}
                        {consumption
                            ?.filter(
                                (c) => c.bunkeringGuid === bunkeringGuid && c.engineDataType === engineType && (!isReadOnly || c.consumptionMt > 0)
                            )
                            ?.sort((a, b) => compareTanks(vessel, a.tankId, b.tankId))
                            ?.map((c, index) => (
                                <div key={`${c.bunkeringGuid}_${c.noonReportId}_${c.engineDataType}_${index}`}>
                                    <ConsumptionData
                                        aolItem={aolItem}
                                        consumption={c}
                                        allConsumption={consumption}
                                        description={getBunkeringDetails(
                                            bunkerings?.find((b) => b.bunkeringGuid === c.bunkeringGuid),
                                            c.tankId
                                        )}
                                        isReadOnly={isReadOnly}
                                        onChange={onChange}
                                        availableRobForTank={getAvailableRob(c.bunkeringGuid, c.tankId)}
                                        availableRobForBunkerRef={getAvailableRob(c.bunkeringGuid)}
                                        previousRobExists={previousRobs.some(
                                            (rob) => rob.bunkeringGuid === c.bunkeringGuid && rob.tankId === c.tankId
                                        )}
                                    />
                                    {!previousRobs.some((rob) => rob.bunkeringGuid === c.bunkeringGuid && rob.tankId === c.tankId) && (
                                        <WarningText>Missing ROB in previous AoL period. Unknown quantity available.</WarningText>
                                    )}
                                </div>
                            ))}
                    </div>
                );
            })}
            {consumption?.filter((c) => c.engineDataType === engineType && c.cost > 0)?.length > 0 && (
                <CostWrapper>
                    <FuelTypeHeaderStyle>Cost</FuelTypeHeaderStyle>
                    <p>{`$${(
                        consumption?.filter((c) => c.engineDataType === engineType).reduce((acc, curr) => acc + (curr.cost ?? 0), 0) ?? 0
                    ).toFixed(1)}`}</p>
                </CostWrapper>
            )}
        </div>
    );
};

export default ConsumptionContainer;
